import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Dialog, Field, Input, Select } from '@/components/ui';
import {
  type ChecksBody,
  type DispensingRx,
  type ScanBody,
  type ScanResult,
  useSecondCheckers,
} from '@/features/admin-v2/api';
import { isApiError } from '@/lib/api';
import {
  ID_LAST4,
  missingChecks,
  requiresCounselling,
  requiresIdCheck,
  requiresSecondCheck,
} from './safety-checks';
import { ScanPanel } from './ScanPanel';
import styles from './DispensaryPage.module.css';

/** The staff PIN: 4 to 8 digits (the API's rule). */
const PIN = /^\d{4,8}$/;

/**
 * The dispensing safety checks of one prescription. RL-1631: for a
 * controlled or psychotropic medicine the ID check (who collects, last 4 of
 * the ID seen) and for a high-alert one the second checker are recorded
 * here; the API decides when they are complete (`needs*` flags). RL-1629:
 * the box scans are recorded through the API from the scan panel; `rx` is
 * the live prescription, so a scan ticks its item at once.
 */
export function SafetyChecksDialog({
  rx,
  busy,
  scanning,
  onClose,
  onSave,
  onScan,
  onRecorded,
  onError,
}: {
  rx: DispensingRx;
  busy: boolean;
  scanning: boolean;
  onScan: (itemId: string, body: ScanBody) => Promise<ScanResult>;
  onClose: () => void;
  onSave: (body: ChecksBody) => Promise<DispensingRx>;
  onRecorded: (updated: DispensingRx) => void;
  onError: (error: unknown) => void;
}) {
  const { t } = useTranslation();
  const needsId = requiresIdCheck(rx);
  const needsSecond = requiresSecondCheck(rx);
  const checkers = useSecondCheckers(rx, needsSecond);
  const needsCounselling = requiresCounselling(rx);
  const [clinicalVerified, setClinicalVerified] = useState(
    rx.clinicalVerified ?? false,
  );
  const [counsellingDone, setCounsellingDone] = useState(rx.counsellingDone);
  const [collector, setCollector] = useState<'patient' | 'other' | ''>(
    rx.idCheck?.collector ?? '',
  );
  const [collectorName, setCollectorName] = useState(
    rx.idCheck?.collectorName ?? '',
  );
  const [idLast4, setIdLast4] = useState(rx.idCheck?.idLast4 ?? '');
  const [secondCheckerId, setSecondCheckerId] = useState(
    rx.secondCheckerId ?? '',
  );
  // RL-1943: the second checker types their own PIN here.
  const [checkerPin, setCheckerPin] = useState('');
  const checkerAlreadyRecorded =
    !!rx.secondCheckerId &&
    !rx.needsSecondCheck &&
    secondCheckerId === rx.secondCheckerId;
  const [refusal, setRefusal] = useState<string | null>(null);
  const [stillMissing, setStillMissing] = useState<string[]>([]);

  const idReady =
    !needsId ||
    (collector !== '' &&
      ID_LAST4.test(idLast4.trim()) &&
      (collector === 'patient' || collectorName.trim().length >= 2));
  const secondReady =
    !needsSecond ||
    checkerAlreadyRecorded ||
    (secondCheckerId !== '' && PIN.test(checkerPin));
  // RL-1632: the required ticks still unticked, in the dialog's order.
  const unticked = [
    ...(clinicalVerified ? [] : ['clinical']),
    ...(needsCounselling && !counsellingDone ? ['counselling'] : []),
  ];
  const ready = unticked.length === 0 && idReady && secondReady;

  // RL-1632: every tick as the dispenser chose it (no fixed `true`s).
  const body = (): ChecksBody => ({
    clinicalVerified,
    counsellingDone,
    ...(needsId && collector !== ''
      ? {
          idCheck: {
            collector,
            ...(collector === 'other'
              ? { collectorName: collectorName.trim() }
              : {}),
            idLast4: idLast4.trim(),
          },
        }
      : {}),
    ...(needsSecond && secondCheckerId && !checkerAlreadyRecorded
      ? { secondCheckerId, secondCheckerPin: checkerPin }
      : {}),
  });

  const save = () => {
    setRefusal(null);
    void onSave(body())
      .then((updated) => {
        const missing = missingChecks(updated);
        setStillMissing(missing);
        if (missing.length === 0) onRecorded(updated);
      })
      .catch((err: unknown) => {
        // A known refusal (422/409) is shown here in plain words.
        if (isApiError(err) && (err.status === 422 || err.status === 409))
          setRefusal(err.message);
        else onError(err);
      });
  };

  const checkerOptions = (checkers.data ?? []).map((c) => ({
    value: c.id,
    label: t(
      c.signedIn ? 'dispensary.checkerOption' : 'dispensary.checkerOptionAway',
      {
        name: c.fullName,
        kind: t(`dispensary.checkerKind.${c.kind}`),
      },
    ),
  }));
  // Keep a recorded checker visible even if they left the list.
  if (
    rx.secondChecker &&
    !checkerOptions.some((o) => o.value === rx.secondChecker?.id)
  )
    checkerOptions.unshift({
      value: rx.secondChecker.id,
      label: rx.secondChecker.fullName ?? rx.secondChecker.id,
    });

  return (
    <Dialog
      open
      onClose={onClose}
      title={t('dispensary.safetyTitle', { number: rx.number })}
      dismissible={!busy}
      footer={
        <>
          {unticked.length > 0 && (
            <span className={styles.sub}>
              {t('dispensary.untickedHint', {
                list: unticked
                  .map((key) => t(`dispensary.missing.${key}`))
                  .join(', '),
              })}
            </span>
          )}
          <Button disabled={busy} onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            loading={busy}
            disabled={!ready}
            onClick={save}
          >
            {t('dispensary.recordChecks')}
          </Button>
        </>
      }
    >
      <div className={styles.safetyForm}>
        <p className={styles.sub}>{t('dispensary.safetyIntro')}</p>
        {refusal && (
          <p className={styles.refusal} role="alert">
            {refusal}
          </p>
        )}
        {stillMissing.length > 0 && (
          <p className={styles.refusal} role="alert">
            {t('dispensary.stillMissing', {
              list: stillMissing
                .map((key) => t(`dispensary.missing.${key}`))
                .join(', '),
            })}
          </p>
        )}
        <ScanPanel rx={rx} scanning={scanning} onScan={onScan} />
        <fieldset className={styles.safetyPanel}>
          <legend>{t('dispensary.safetyColumn')}</legend>
          <ul className={styles.checkList}>
            <li>
              <label className={styles.checkItem}>
                <input
                  type="checkbox"
                  checked={clinicalVerified}
                  onChange={(event) =>
                    setClinicalVerified(event.target.checked)
                  }
                />
                {t('dispensary.clinicalVerified')}
              </label>
            </li>
            <li>
              <label className={styles.checkItem}>
                <input
                  type="checkbox"
                  checked={counsellingDone}
                  onChange={(event) => setCounsellingDone(event.target.checked)}
                />
                {t('dispensary.counsellingDone')}
              </label>
            </li>
          </ul>
        </fieldset>
        {needsId && (
          <fieldset className={styles.safetyPanel}>
            <legend>{t('dispensary.idCheckTitle')}</legend>
            <p className={styles.sub}>{t('dispensary.idCheckHelp')}</p>
            <div
              className={styles.choiceRow}
              role="radiogroup"
              aria-label={t('dispensary.collector')}
            >
              <label className={styles.checkItem}>
                <input
                  type="radio"
                  name="collector"
                  checked={collector === 'patient'}
                  onChange={() => setCollector('patient')}
                />
                {t('dispensary.collectorPatient')}
              </label>
              <label className={styles.checkItem}>
                <input
                  type="radio"
                  name="collector"
                  checked={collector === 'other'}
                  onChange={() => setCollector('other')}
                />
                {t('dispensary.collectorOther')}
              </label>
            </div>
            {collector === 'other' && (
              <Field label={t('dispensary.collectorName')}>
                <Input
                  value={collectorName}
                  maxLength={120}
                  autoComplete="off"
                  onChange={(event) => setCollectorName(event.target.value)}
                />
              </Field>
            )}
            <Field
              label={t('dispensary.idLast4')}
              hint={t('dispensary.idLast4Hint')}
            >
              <Input
                value={idLast4}
                maxLength={4}
                inputMode="text"
                autoComplete="off"
                className={styles.short}
                onChange={(event) =>
                  setIdLast4(event.target.value.toUpperCase())
                }
              />
            </Field>
          </fieldset>
        )}
        {needsSecond && (
          <fieldset className={styles.safetyPanel}>
            <legend>{t('dispensary.secondCheckTitle')}</legend>
            <p className={styles.sub}>{t('dispensary.secondCheckHelp')}</p>
            <Field
              label={t('dispensary.secondChecker')}
              error={
                checkers.isError
                  ? isApiError(checkers.error)
                    ? checkers.error.message
                    : t('dispensary.checkersFailed')
                  : undefined
              }
              hint={
                checkers.data && checkers.data.length === 0
                  ? t('dispensary.noCheckers')
                  : undefined
              }
            >
              <Select
                value={secondCheckerId}
                placeholder={
                  checkers.isLoading
                    ? t('dispensary.checkersLoading')
                    : t('dispensary.chooseChecker')
                }
                options={checkerOptions}
                onChange={(event) => {
                  setSecondCheckerId(event.target.value);
                  setCheckerPin('');
                }}
              />
            </Field>
            {secondCheckerId !== '' && !checkerAlreadyRecorded && (
              <Field
                label={t('dispensary.checkerPin')}
                hint={t('dispensary.checkerPinHint')}
              >
                <Input
                  type="password"
                  inputMode="numeric"
                  autoComplete="off"
                  maxLength={8}
                  value={checkerPin}
                  className={styles.short}
                  onChange={(event) =>
                    setCheckerPin(event.target.value.replace(/\D/g, ''))
                  }
                />
              </Field>
            )}
          </fieldset>
        )}
      </div>
    </Dialog>
  );
}
